import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "@/lib/utils";

const NAV = [
  { to: "/", label: "Index", num: "01" },
  { to: "/work", label: "Work", num: "02" },
  { to: "/studio", label: "Studio", num: "03" },
  { to: "/contact", label: "Contact", num: "04" },
];

export default function Nav({ onStartProject }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <header
      className={cn(
        "fixed top-0 left-0 right-0 z-50 bg-ivory transition-[border-color,padding] duration-500",
        scrolled ? "border-b border-charcoal/15 py-3" : "border-b border-transparent py-6"
      )}
      data-testid="site-nav"
    >
      <div className="mx-auto max-w-[1600px] px-6 md:px-12 flex items-center justify-between">
        <Link to="/" className="flex items-baseline gap-2 group" data-testid="nav-logo">
          <span className="font-display text-2xl md:text-3xl leading-none tracking-tightest">
            Bosecrafts
          </span>
          <span className="hidden md:inline text-[10px] uppercase tracking-[0.25em] text-stone-muted">
            &nbsp;— Est. Siliguri
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-10">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === "/"}
              data-testid={`nav-link-${n.label.toLowerCase()}`}
              className={({ isActive }) =>
                cn(
                  "group flex items-baseline gap-2 text-[13px] tracking-[0.05em]",
                  isActive ? "text-charcoal" : "text-stone-muted hover:text-charcoal"
                )
              }
            >
              <span className="font-mono text-[10px] opacity-60">{n.num}</span>
              <span className="u-link">{n.label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-4">
          <div className="hidden lg:flex items-center gap-2 text-[11px] tracking-[0.18em] uppercase text-stone-muted">
            <span className="status-dot" />
            <span>Available — Q3 2026</span>
          </div>
          <button
            onClick={onStartProject}
            data-testid="nav-start-project"
            className="hidden md:inline-flex items-center gap-2 rounded-none border border-charcoal bg-charcoal px-5 py-2.5 text-[12px] uppercase tracking-[0.2em] text-ivory transition-colors duration-300 hover:bg-ivory hover:text-charcoal"
          >
            Start a Project
            <span className="text-base leading-none">↗</span>
          </button>
          <button
            className="md:hidden text-xs uppercase tracking-[0.25em]"
            onClick={() => setOpen((v) => !v)}
            data-testid="nav-menu-toggle"
            aria-label="Menu"
          >
            {open ? "Close" : "Menu"}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0 }}
            animate={{ height: "auto" }}
            exit={{ height: 0 }}
            transition={{ duration: 0.4, ease: [0.65, 0, 0.35, 1] }}
            className="md:hidden overflow-hidden border-t border-charcoal/10"
          >
            <div className="px-6 py-8 flex flex-col gap-6 bg-ivory">
              {NAV.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  end={n.to === "/"}
                  data-testid={`nav-mobile-${n.label.toLowerCase()}`}
                  className="flex items-baseline gap-4 font-serif text-3xl"
                >
                  <span className="font-mono text-[11px] opacity-60">{n.num}</span>
                  {n.label}
                </NavLink>
              ))}
              <button
                onClick={onStartProject}
                data-testid="nav-mobile-start"
                className="mt-4 self-start border border-charcoal bg-charcoal px-6 py-3 text-[11px] uppercase tracking-[0.22em] text-ivory"
              >
                Start a project ↗
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
